import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';

const ModernShiftPicker = ({
  index,
  shift,
  onShiftChange,
  onRemove,
  canRemove,
  errors
}) => {
  // Calculate minimum date (2 days from now)
  const minDate = moment().add(2, 'days').startOf('day').toDate();

  const handleDateChange = (date) => {
    if (!date) return;
  
    // If we already have startTime/endTime, keep the time part when changing date
    const updatedStartTime = shift.startTime
      ? moment(date).set({
          hour: moment(shift.startTime).hour(),
          minute: moment(shift.startTime).minute(),
        }).toDate()
      : null;
  
    const updatedEndTime = shift.endTime
      ? moment(date).set({
          hour: moment(shift.endTime).hour(),
          minute: moment(shift.endTime).minute(),
        }).toDate()
      : null;
  
    onShiftChange({
      ...shift,
      date: date,
      startTime: updatedStartTime,
      endTime: updatedEndTime,
    });
  };

  const handleStartTimeChange = (time) => {
    if (!time || !shift.date) return;
  
    const combinedStartTime = moment(shift.date).set({
      hour: moment(time).hour(),
      minute: moment(time).minute(),
    }).toDate();
  
    onShiftChange({ ...shift, startTime: combinedStartTime });
  };

  const handleEndTimeChange = (time) => {
    if (!time || !shift.date) return;
  
    const combinedEndTime = moment(shift.date).set({
      hour: moment(time).hour(),
      minute: moment(time).minute(),
    }).toDate();
  
    onShiftChange({ ...shift, endTime: combinedEndTime });
  };

  const handleStaffsRequiredChange = (e) => {
    onShiftChange({ 
      ...shift, 
      staffsRequired: parseInt(e.target.value, 10) || 1 
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-4 shadow-sm transition-all hover:shadow-md">
      <div className="flex justify-between items-center mb-5 pb-3 border-b border-gray-100">
        <h3 className="text-base font-medium text-gray-900">Shift {index + 1}</h3>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="flex items-center justify-center w-7 h-7 rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
            aria-label="Remove shift"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Date Field */}
        <div className="flex flex-col">
          <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
          <DatePicker
            selected={shift.date ? moment(shift.date).toDate() : null}
            onChange={handleDateChange}
            minDate={minDate}
            dateFormat="MMM dd, yyyy"
            placeholderText="Select date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
          {errors && errors.date && (
            <span className="text-red-500 text-xs mt-1">{errors.date.message}</span>
          )}
        </div>

        {/* Start Time Field */}
        <div className="flex flex-col">
          <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
          <DatePicker
            selected={shift.startTime ? moment(shift.startTime).toDate() : null}
            onChange={handleStartTimeChange}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={30}
            timeCaption="Time"
            dateFormat="h:mm aa"
            placeholderText="Start time"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            disabled={!shift.date}
          />
          {errors && errors.startTime && (
            <span className="text-red-500 text-xs mt-1">{errors.startTime.message}</span>
          )}
        </div>

        {/* End Time Field */}
        <div className="flex flex-col">
          <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
          <DatePicker
            selected={shift.endTime ? moment(shift.endTime).toDate() : null}
            onChange={handleEndTimeChange}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={30}
            timeCaption="Time"
            dateFormat="h:mm aa"
            placeholderText="End time"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            disabled={!shift.date}
          />
          {errors && errors.endTime && (
            <span className="text-red-500 text-xs mt-1">{errors.endTime.message}</span>
          )}
        </div>

        {/* Officers Required Field */}
        <div className="flex flex-col">
          <label className="block text-xs font-medium text-gray-700 mb-1">Officers</label>
          <input
            type="number"
            min="1"
            value={shift.staffsRequired || 1}
            onChange={handleStaffsRequiredChange}
            placeholder="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
          {errors && errors.staffsRequired && (
            <span className="text-red-500 text-xs mt-1">{errors.staffsRequired.message}</span>
          )}
        </div>
      </div>

      <div className="mt-3">
        <p className="text-xs text-gray-500">Shifts must be scheduled at least 2 days in advance.</p>
      </div>
    </div>
  );
};

export default ModernShiftPicker;
